import backendCall from './AxiosService';
import { z } from 'zod';
import {
  temperatureSettingsSchema,
  automationSettingsSchema,
  fuelSettingsSchema,
  appSettingsSchema,
  utilizationSettingsSchema,
  type TemperatureSettings,
  type AutomationSettings,
  type FuelSettings,
  type AppSettings,
  type UtilizationSettings
} from '../schemas/settings';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    message: string;
    details?: string;
  }
}

// Schema for unit questioning settings (subset available to yard check users)
export const unitQuestioningSettingsSchema = z.object({
  unit_questioning_enabled: z.boolean(),
  unit_questioning_skippable: z.boolean(),
  questioning_trigger: z.number()
});

export type UnitQuestioningSettings = z.infer<typeof unitQuestioningSettingsSchema>;

// Schema for unit check enforcement settings (subset available to train workers)
export const unitCheckEnforcementSettingsSchema = z.object({
  unit_check_enforcement_enabled: z.boolean()
});

export type UnitCheckEnforcementSettings = z.infer<typeof unitCheckEnforcementSettingsSchema>;

/**
 * Fetch unit check enforcement setting (accessible to train workers without admin permissions)
 * This is used by the TrainOutcomeModal to check if enforcement is enabled.
 */
export async function getUnitCheckEnforcementSettings(): Promise<UnitCheckEnforcementSettings> {
  try {
    const response = await backendCall.get<ApiResponse<UnitCheckEnforcementSettings>>('/settings/unit-check-enforcement');

    if (response.data && response.data.success) {
      // Validate response against schema
      return unitCheckEnforcementSettingsSchema.parse(response.data.data);
    }

    // Return default (enabled) if API call fails
    return {
      unit_check_enforcement_enabled: true
    };
  } catch (error) {
    console.error('Error fetching unit check enforcement settings:', error);
    // Return default (enabled) if API call fails
    return {
      unit_check_enforcement_enabled: true
    };
  }
}

/**
 * Fetch temperature settings
 */
export async function getTemperatureSettings(): Promise<TemperatureSettings> {
  try {
    const response = await backendCall.get<ApiResponse<TemperatureSettings>>('/settings/temperature');
    
    if (response.data && response.data.success) {
      // Validate response against schema
      return temperatureSettingsSchema.parse(response.data.data);
    }
    
    // Return schema defaults if API call fails
    return temperatureSettingsSchema.parse({});
  } catch (error) {
    console.error('Error fetching temperature settings:', error);
    // Return schema defaults if API call fails
    return temperatureSettingsSchema.parse({});
  }
}

/**
 * Update temperature settings
 */
export async function updateTemperatureSettings(settings: Partial<TemperatureSettings>): Promise<ApiResponse<TemperatureSettings>> {
  try {
    // Validate input data
    const validatedSettings = temperatureSettingsSchema.partial().parse(settings);
    // Backend expects PUT at /api/v1/settings/temperature
    const response = await backendCall.put<ApiResponse<TemperatureSettings>>('/settings/temperature', validatedSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating temperature settings:', error);
    throw error;
  }
}

/**
 * Fetch unit questioning settings (accessible to yard check users without admin permissions)
 * This is a subset of automation + temperature settings needed for the unit questioning feature.
 */
export async function getUnitQuestioningSettings(): Promise<UnitQuestioningSettings> {
  try {
    const response = await backendCall.get<ApiResponse<UnitQuestioningSettings>>('/settings/unit-questioning');

    if (response.data && response.data.success) {
      // Validate response against schema
      return unitQuestioningSettingsSchema.parse(response.data.data);
    }

    // Return defaults if API call fails
    return {
      unit_questioning_enabled: false,
      unit_questioning_skippable: true,
      questioning_trigger: 10.0
    };
  } catch (error) {
    console.error('Error fetching unit questioning settings:', error);
    // Return defaults if API call fails
    return {
      unit_questioning_enabled: false,
      unit_questioning_skippable: true,
      questioning_trigger: 10.0
    };
  }
}

/**
 * Fetch automation settings
 */
export async function getAutomationSettings(): Promise<AutomationSettings> {
  try {
    const response = await backendCall.get<ApiResponse<AutomationSettings>>('/settings/automation');
    
    if (response.data && response.data.success) {
      // Validate response against schema
      return automationSettingsSchema.parse(response.data.data);
    }
    
    // Return schema defaults if API call fails
    return automationSettingsSchema.parse({});
  } catch (error) {
    console.error('Error fetching automation settings:', error);
    // Return schema defaults if API call fails
    return automationSettingsSchema.parse({});
  }
}

/**
 * Update automation settings
 */
export async function updateAutomationSettings(settings: Partial<AutomationSettings>): Promise<ApiResponse<AutomationSettings>> {
  try {
    // Validate input data
    const validatedSettings = automationSettingsSchema.partial().parse(settings);
    const response = await backendCall.post<ApiResponse<AutomationSettings>>('/settings/automation', validatedSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating automation settings:', error);
    throw error;
  }
}

/**
 * Fetch fuel settings
 */
export async function getFuelSettings(): Promise<FuelSettings> {
  try {
    const response = await backendCall.get<ApiResponse<FuelSettings>>('/settings/fuel');
    
    if (response.data && response.data.success) {
      // Validate response against schema
      return fuelSettingsSchema.parse(response.data.data);
    }
    
    // Return schema defaults if API call fails
    return fuelSettingsSchema.parse({});
  } catch (error) {
    console.error('Error fetching fuel settings:', error);
    // Return schema defaults if API call fails
    return fuelSettingsSchema.parse({});
  }
}

/**
 * Update fuel settings
 */
export async function updateFuelSettings(settings: Partial<FuelSettings>): Promise<ApiResponse<FuelSettings>> {
  try {
    // Validate input data
    const validatedSettings = fuelSettingsSchema.partial().parse(settings);
    const response = await backendCall.post<ApiResponse<FuelSettings>>('/settings/fuel', validatedSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating fuel settings:', error);
    throw error;
  }
}

/**
 * Fetch app settings
 */
export async function getAppSettings(): Promise<AppSettings> {
  try {
    const response = await backendCall.get<ApiResponse<AppSettings>>('/settings/app');
    
    if (response.data && response.data.success) {
      // Validate response against schema
      return appSettingsSchema.parse(response.data.data);
    }
    
    // Return schema defaults if API call fails
    return appSettingsSchema.parse({});
  } catch (error) {
    console.error('Error fetching app settings:', error);
    // Return schema defaults if API call fails
    return appSettingsSchema.parse({});
  }
}

/**
 * Update app settings
 */
export async function updateAppSettings(settings: Partial<AppSettings>): Promise<ApiResponse<AppSettings>> {
  try {
    // Validate input data
    const validatedSettings = appSettingsSchema.partial().parse(settings);
    const response = await backendCall.post<ApiResponse<AppSettings>>('/settings/app', validatedSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating app settings:', error);
    throw error;
  }
}

/**
 * Fetch utilization threshold settings
 * Used for color-coding shift utilization percentages
 */
export async function getUtilizationSettings(): Promise<UtilizationSettings> {
  try {
    const response = await backendCall.get<ApiResponse<UtilizationSettings>>('/settings/utilization');

    if (response.data && response.data.success) {
      // Validate response against schema
      return utilizationSettingsSchema.parse(response.data.data);
    }

    // Return schema defaults if API call fails
    return utilizationSettingsSchema.parse({});
  } catch (error) {
    console.error('Error fetching utilization settings:', error);
    // Return schema defaults if API call fails
    return utilizationSettingsSchema.parse({});
  }
}

/**
 * Update utilization threshold settings
 */
export async function updateUtilizationSettings(settings: Partial<UtilizationSettings>): Promise<ApiResponse<UtilizationSettings>> {
  try {
    // Validate input data
    const validatedSettings = utilizationSettingsSchema.partial().parse(settings);
    // Backend expects PUT at /api/v1/settings/utilization
    const response = await backendCall.put<ApiResponse<UtilizationSettings>>('/settings/utilization', validatedSettings);
    return response.data;
  } catch (error) {
    console.error('Error updating utilization settings:', error);
    throw error;
  }
}
